/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { getBackendUrl } from "@Utils";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { renderToStaticMarkup } from "react-dom/server";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import { updateGroupsData } from "@Actions/portalSetting";
import { getPortalProfiles } from "@Actions/portalSetting.helper";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { AddUsersToProject } from "@Features/projects";
import { showToastNotification } from "@Reducers/toastSlice";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import RolesModalContent from "./RolesModalContent";
import BulkUpdateRolesModal from "./BulkUpdateRolesModal";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

function Members() {
  const { t } = useTranslation();
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openBulkUpdateModal, setOpenBulkUpdateModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [bulkSelectedRole, setBulkSelectedRole] = useState({});
  const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [removeUsersPayload, setRemoveUsersPayload] = useState<any>({});
  const [toolbarButtonState, setToolbarButtonState] = useState({
    remove: true,
    bulkUpdate: true,
  });
  const dispatch = useDispatch();
  const roleDataRef = useRef<any>({});
  const AutoCompleteAny = AutoComplete as any;

  const { portalSettingInfo } = useSelector((state: RootState) => state);
  const canEditGroups = !!portalSettingInfo?.groups?.editGroups?.enable;

  const roleData = useSelector((state: RootState) => state.profileData);
  const dataGrid = useRef<any>(null);
  const userDataGrid = useRef<any>(null);
  // Route params enter from outside the type system; the backend route schema validates these ids.
  const _routeParams = useParams<{ groupId: ObjectIdString }>();
  const paraGrpId = useParams<{ costDatabaseId: ObjectIdString }>();
  const params = { groupId: paraGrpId.costDatabaseId || _routeParams.groupId };

  const remoteDataSource = `${getBackendUrl()}/groups/getGroups`;
  const query = useMemo(() => (params.groupId ? { params: { groupId: params.groupId } } : {}), [params.groupId]);

  useEffect(() => {
    roleDataRef.current = roleData;
  }, [roleData]);

  const onSelectedRowIdsChanged = (args: any) => {
    const selectedIds = args?.detail?.args?.selectedRowIds || [];
    const totalRowsSelected = selectedIds.length;

    if (totalRowsSelected === 0) {
      setToolbarButtonState({ remove: true, bulkUpdate: true });
    } else {
      setToolbarButtonState({ remove: false, bulkUpdate: false });
    }
  };

  const toolbarClickHandler = useCallback(
    async (toolClicked: string) => {
      if (toolClicked === "add_user") {
        setOpenAddUserModal(true);
      }
      if (toolClicked === "remove_user") {
        const selectedData = userDataGrid.current?.dataView?.getAllSelectedItems() || [];
        const userIds = selectedData.map((user: any) => user.userId);
        setOpenRemoveUserModal(true);
        setRemoveUsersPayload({
          data: userIds,
          action: "remove_user",
          groupId: params.groupId,
          removeFromResources: false,
        });
      }
      if (toolClicked === "bulk_update") {
        setOpenBulkUpdateModal(true);
        setSelectedRowData(userDataGrid.current?.dataView?.getAllSelectedItems() || []);
      }
    },
    [params.groupId],
  );

  const handleCancelAddUsersModal = () => {
    setNextEnabled(false);
    setOpenAddUserModal(false);
    setOpenBulkUpdateModal(false);
    dataGrid.current?.refresh();
    userDataGrid.current?.refresh();
  };

  const handleAddClick = async (selectedData: any) => {
    const groupId = params.groupId;
    const data = {
      data: selectedData,
      action: "insert" as const,
      groupId,
    };
    const response = await updateGroupsData(data);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The selected user(s) have been added successfully."),
        }),
      );
    }
    handleCancelAddUsersModal();
    userDataGrid.current?.refresh();
  };

  const setRoleForAllUsers = async () => {
    const response = await updateGroupsData(bulkSelectedRole);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The roles of selected user(s) have been updated successfully."),
        }),
      );
    }
    handleCancelAddUsersModal();
  };

  const handleBulkRuleChange = (e: any) => {
    const userIds = selectedRowData.map((data: any) => data.userId);
    const roleDataPayload = {
      roleChangedTo: e.itemData?.frontendName,
      roleId: e.value,
      userId: userIds,
    };
    const groupId = params.groupId;
    const data = {
      data: roleDataPayload,
      action: "update" as const,
      groupId,
    };
    setBulkSelectedRole(data);
    setNextEnabled(true);
  };

  const AssignRolesModalContent = () =>
    RolesModalContent({
      value: bulkSelectedRole,
      dataSource: roleData,
      onChange: handleBulkRuleChange,
    });

  const handleAddEditModalCLose = () => {
    setOpenRemoveUserModal(false);
    userDataGrid.current?.refresh();
  };

  const handleCheckboxChange = (args: any) => {
    setRemoveUsersPayload((prev: any) => ({
      ...prev,
      removeFromResources: args.checked,
    }));
  };

  const removeUsers = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={
        <div className="flex justify-normal gap-4 pb-4 ps-4">
          <ButtonComponent buttonType="gradientButton" onClick={removeUsersFromGroup}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent
            buttonType="borderButton"
            onClick={() => {
              setOpenRemoveUserModal(false);
            }}
          >
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
      width="30%"
      headerTitle={
        <div className="flex justify-between w-full">
          <span>{t("Remove User(s)")}</span>
        </div>
      }
      onCancel={handleAddEditModalCLose}
      isVisible={openRemoveUserModal}
      modelContent={
        <div className="flex flex-col justify-center ms-4 pb-4 me-1 ">
          <div className="mb-4">{t("Are you sure you want to remove the selected user(s) from the group?")}</div>
          <div className="font-bold">
            {t(
              "Note:- Removing this user from the group will result in the removal of that user from all the related entities.",
            )}
          </div>
          <span>
            {" "}
            <CheckBoxComponent
              label="Also, remove the user from the projects/ spaces they're part of"
              id="deleteTask"
              change={handleCheckboxChange}
            />{" "}
          </span>
        </div>
      }
    />
  );

  const updateRolesBulk = () =>
    BulkUpdateRolesModal({
      headerText: t("Update Role(s)"),
      closeModal: handleCancelAddUsersModal,
      modalState: true,
      confirmClick: setRoleForAllUsers,
      confirmEnabled: nextEnabled,
      modalContent: AssignRolesModalContent,
    });

  useEffect(() => {
    dispatch(getPortalProfiles());
  }, []);

  const addButtonTemplate = useCallback(
    () => (
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="add_user" disabled={!canEditGroups}>
        <div className="flex justify-center items-center gap-2">
          <PlusIcon color={"var(--primary-text)"} />
          <span>{t("Add")}</span>
        </div>
      </ButtonComponent>
    ),
    [canEditGroups],
  );

  const deleteBtnTemplate = useCallback(
    () => (
      <div>
        <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="remove_user" disabled={toolbarButtonState.remove}>
          <span className="flex flex-row items-center justify-start ">
            <DeleteIcon size="15" className="me-2" />
            {t("Remove")}
          </span>
        </ButtonComponent>
      </div>
    ),
    [toolbarButtonState.remove],
  );

  const bulkUpdtTemplate = useCallback(
    () => (
      <div>
        <ButtonComponent
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          id="bulk_update"
          disabled={toolbarButtonState.bulkUpdate}
        >
          <span className="flex flex-row items-center justify-start ">
            <IconComponent name="Update" width="1.5rem" height="1.5rem" className="me-2" />
            {t("Bulk Update Roles")}
          </span>
        </ButtonComponent>
      </div>
    ),
    [toolbarButtonState.bulkUpdate],
  );

  const toolbarOptions = useMemo(
    () => [
      { id: "add_user", template: addButtonTemplate },
      { id: "remove_user", template: deleteBtnTemplate, disabled: toolbarButtonState.remove },
      { id: "bulk_update", template: bulkUpdtTemplate, disabled: toolbarButtonState.bulkUpdate },
    ],
    [toolbarButtonState.remove, toolbarButtonState.bulkUpdate, addButtonTemplate, deleteBtnTemplate, bulkUpdtTemplate],
  );

  const removeUsersFromGroup = async () => {
    setOpenRemoveUserModal(false);
    const res = await updateGroupsData(removeUsersPayload);
    if (res) {
      userDataGrid.current?.refresh();
    }
  };

  async function handleChange(e: any, props: any) {
    const roleDataPayload = {
      roleChangedTo: e.target.typedString,
      roleId: e.target.value,
      userId: props.userId,
      selectedUserfromRow: props.name,
    };
    const groupId = params.groupId;
    const data = {
      data: roleDataPayload,
      action: "update" as const,
      groupId,
    };
    await updateGroupsData(data);
    handleCancelAddUsersModal();
  }

  const portalRoleFormatter = (_row: any, _cell: any, _value: any, colDef: any, dataContext: any) => {
    const container = document.createElement("div");
    container.style.width = "100%";
    const options = roleDataRef.current || [];
    const root = createRoot(container);
    root.render(
      <AutoCompleteAny
        value={options.find((opt: any) => opt.id === dataContext.portalRole) || null}
        options={options}
        valueField="id"
        getOptionLabel={(option: any) => option?.frontendName || ""}
        onChange={(_event: any, newValue: any) =>
          handleChange({ target: { typedString: newValue?.frontendName, value: newValue?.id } }, dataContext)
        }
        disabled={!canEditGroups}
        renderInput={(params: any) => <TextFieldComponent {...params} />}
      />,
    );
    return container;
  };

  const openAddUsersModal = () => {
    setOpenAddUserModal(!openAddUserModal);
  };

  const nameHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Name")} />);
  const portalRoleHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Dropdown" label={t("Portal Role")} />);
  const designationHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Designation")} />);
  const organisationHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Organisation")} />);
  const addedOnHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Date and Time" label={t("Added on")} />);

  const columnDefinitions = useMemo(
    () => [
      {
        id: "_id",
        field: "_id",
        name: "_id",
        hidden: true,
      },
      {
        id: "name",
        field: "name",
        name: t("Name"),
        width: 180,
        originalWidth: 180,
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: nameHeaderRenderer,
      },
      {
        id: "portalRoleName",
        field: "portalRoleName",
        name: t("Portal Role"),
        width: 220,
        originalWidth: 220,
        formatter: portalRoleFormatter,
        disableSorting: true,
        disableFiltering: true,
        disableGrouping: true,
        headerRenderer: portalRoleHeaderRenderer,
      },
      {
        id: "designation",
        field: "designation",
        name: t("Designation"),
        width: 180,
        originalWidth: 180,
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: designationHeaderRenderer,
      },
      {
        id: "organisation",
        field: "organisation",
        name: t("Organisation"),
        width: 180,
        originalWidth: 180,
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: organisationHeaderRenderer,
      },
      {
        id: "createdDate",
        field: "createdDate",
        name: t("Added on"),
        width: 180,
        originalWidth: 180,
        formatterType: "Date",
        filterType: "Date",
        clipMode: "EllipsisWithTooltip",
        headerRenderer: addedOnHeaderRenderer,
      },
    ],

    [canEditGroups],
  );

  const groupMembers = () => (
    <div className="overflow-auto h-full">
      <SlickGrid1
        ref={userDataGrid}
        columnDefinitions={columnDefinitions as any}
        remoteDataSource={remoteDataSource}
        query={query}
        toolbar={toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={onSelectedRowIdsChanged}
        enableFilteringOption
        enableSortingOption
        enableGroupingOption
        enableColumnChooser
        enableRowResize
        rowHeight={68 as any}
        excelExport
        hideNoDataIllustration
      />
    </div>
  );

  const AddUsersAny = AddUsersToProject as any;

  return (
    <div className="w-full flex flex-col h-full overflow-auto">
      {roleData && roleData.length && groupMembers()}
      {openAddUserModal && (
        <AddUsersAny
          source={"portalSetting"}
          closeModal={openAddUsersModal}
          customOnNext={handleAddClick}
          groupId={params.groupId}
          customHeader="Add Users to Group"
        />
      )}
      {openBulkUpdateModal && updateRolesBulk()}
      {openRemoveUserModal && removeUsers()}
    </div>
  );
}
Members.displayName = "Members";
export default Members;
